/* eslint-disable @typescript-eslint/naming-convention */

import axios from 'axios'

import { getVGSDomain } from '../config/config'
import { toCamelCase, toSnakeCase } from '../helpers/utils'
import { Address } from './address'
import { CoreDocument } from './document'
import { EquipmentType } from './equipment'
import { Team } from './team'

export interface ListUser {
  id: string
  firstName: string
  middleName?: string | null
  lastName: string
  mobile?: string
  mobileVerified?: boolean
  dateJoined?: string
  isActive?: boolean
  isStaff?: boolean
  email?: string
  emailVerified?: boolean
  isTeamAdmin?: boolean
  isSuperuser?: boolean
  isTeamDriver?: boolean
  deprecatedIsTeamAdmin?: boolean
  deprecatedIsTeamDriver?: boolean
  lastLogin?: string
  roles?: UserRoles[]
  defaultTeamMembership?: TeamMembership
  shippingAddress: Address | null
  backgroundCheckStatus?: OnboardingBackgroundCheckStatus
}

export interface User extends ListUser {
  driver: Driver
  hasTenstreetDocument?: boolean
  dateCloseAccountRequested?: string
  team?: string
  invitedUser?: Partial<User>
  location?: Location
  featureFlags?: Record<string, boolean>
  intercomIdentity?: string
  searchLoadingMessage?: string
  equipmentTypes?: EquipmentType[]
  permissions?: any // Permissions  // TODO: Fix camelize to ignore camelising permissions keys
  hadPrivilegeDeniedDetail?: string
  hadPrivilegeSuspendedDetail?: string
  hasEmploymentJobs?: boolean
  hasRecordedTermsOnStripe?: boolean
  deviceVersions?: DeviceVersion[]
}

export interface Driver {
  dob?: string
  status?: DriverStatus
  licenseExpiration?: string
  licenseIssued?: string
  licenseNumber?: string
  licenseState?: string
  licenseFront?: string
  licenseBack?: string
  licenseDocument?: CoreDocument<{}>
  // licenseDocument?: CoreDocument<{}> & { // TODO fix all uppercase properties
  //   metadata: {
  //     DRIVER_LICENSE_ISSUE_DATE: string
  //     DRIVER_LICENSE_NUMBER: string
  //     DRIVER_LICENSE_STATE: string
  //   }
  // }
  dateCreated?: string
  dateUpdated?: string
  isDriverApplicationFormComplete?: boolean
  isDriverInfoComplete?: boolean
  isLicenseInfoComplete?: boolean
  isEmergencyContactComplete?: boolean
  dateTermsAccepted?: string
  medicalCard?: string
  medicalCardDocument?: CoreDocument<{}>
  medicalCardExpiration?: string
  isMedicalCardValid?: boolean
  emergencyContact?: EmergencyContact
  shirtSize?: ShirtSize
  yearsDryVan?: number
  yearsFlatbed?: number
  yearsReefer?: number
  hadAtFaultIncident?: boolean
  hadMajorViolation?: boolean
  hadPrivilegeDenied?: boolean
  hadPrivilegeSuspended?: boolean
  hasSsn?: boolean
  isEarlyCycleDriver?: boolean
}

export interface TeamMembership {
  id: string
  driverStatus: DriverStatus
  isTeamAdmin: boolean
  isTeamDriver: boolean
  driverAssignedTruck?: TeamMembershipTruck
}

export interface TeamMembershipTruck {
  dateCreated: string
  dateUpdated: string
  id: string
  licensePlateNumber: string
  licensePlateState: string
  ownershipType: string
  status: string
  vehicleMake: string
  vehicleModel: string
  vehicleNumber: string
  vehicleYear: string
  vinNumber: string
}

export interface EmergencyContact {
  email: string
  mobile: string
  name: string
  relationship: string
}

export interface Location {
  lat: number
  lng: number
  address: string
}

export interface DeviceVersion {
  appBuild: string
  appVersion: string
  deviceModel: string
  deviceType: string
  latestTimestamp: string
  osVersion: string
}

export enum UserRoles {
  BUSINESS_OWNER = 'BUSINESS_OWNER',
  TEAM_ADMIN = 'TEAM_ADMIN',
  DRIVER = 'DRIVER',
}

export enum DriverStatus {
  ELIGIBLE = 'ELIGIBLE',
  APPLYING = 'APPLYING',
  ONBOARDING = 'ONBOARDING',
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  SUSPENDED_TEMPORARILY = 'SUSPENDED_TEMPORARILY',
  DEACTIVATED = 'DEACTIVATED',
}

export const DriverStatusToHumanReadable: Record<string, string> = {
  ACTIVE: 'Unsuspended',
  SUSPENDED_TEMPORARILY: 'Suspended',
  DEACTIVATED: 'Deactivated',
}

export enum OnboardingBackgroundCheckStatus {
  VERIFIED = 'VERIFIED',
  PENDING = 'PENDING',
  ACTION_REQUIRED = 'ACTION_REQUIRED',
  WAITING_FOR_INVITATION = 'WAITING_FOR_INVITATION',
}

export enum ShirtSize {
  SMALL = 'S',
  MEDIUM = 'M',
  LARGE = 'L',
  X_LARGE = 'XL',
  XX_LARGE = 'XXL',
  XXX_LARGE = 'XXXL',
}

// API

export interface DriverPayload {
  // licenseExpiration?: string
  // licenseNumber?: string
  // licenseState?: string
  // licenseIssued?: string
  // dob?: string
  // ssnEncrypted?: string
  // dateTermsAccepted?: string
  // yearsDryVan?: number
  // yearsFlatbed?: number
  // yearsReefer?: number
  // hadAtFaultIncident?: boolean
  // hadMajorViolation?: boolean
  // hadPrivilegeDenied?: boolean
  // hadPrivilegeSuspended?: boolean
  // hadPrivilegePayload?: boolean
}

export interface UserPayload {
  // firstName?: string
  // lastName?: string
  mobile?: string
  email?: string
  // isTeamDriver?: boolean
  // driver?: DriverPayload
  // isTeamAdmin?: boolean
  shippingAddress?: Address
  // hadPrivilegeDeniedDetail?: string
  // hadPrivilegeSuspendedDetail?: string
}

export function getLoggedInUser(): Promise<{ user: User; team?: Team }> {
  const url = `/api/v3/users/me/?slim_member=true`
  return axios.get(url).then((response) => {
    const { team_object, ...user } = response.data || {}

    return {
      user: toCamelCase(user),
      team: toCamelCase(team_object),
    } as { user: User; team?: Team }
  })
}

export function getUser(user_id: string): Promise<{ user: User; team?: Team }> {
  const url = `/api/v3/users/${user_id}/`
  return axios.get(url).then((response) => {
    const { team_object, ...user } = response.data || {}

    return {
      user: toCamelCase(user),
      team: toCamelCase(team_object),
    } as { user: User; team?: Team }
  })
}

export function updateUser(
  user_id: string,
  payload: UserPayload,
  options: { vgs?: boolean } = {}
): Promise<User> {
  const url = `${options.vgs ? getVGSDomain() : ''}/api/v3/users/${user_id}/`
  const patchOptions = options.vgs ? { withCredentials: true } : undefined
  return axios
    .patch(url, toSnakeCase(payload), patchOptions)
    .then((response) => toCamelCase(response.data) as User)
}

export function updateEmergencyContactInfo(
  userId: string,
  emergencyContact: EmergencyContact
): Promise<User> {
  const url = `/api/v3/users/${userId}/emergency-contact/`
  return axios.post(url, toSnakeCase(emergencyContact)).then((response) => toCamelCase(response.data) as User)
}

interface RevealSsn {
  ssnEncrypted: string
}

export function revealSsn(userId: string): Promise<RevealSsn> {
  const url = `${getVGSDomain()}/api/v3/users/${userId}/ssn/reveal/`
  return axios
    .post(url, null, { withCredentials: true })
    .then((response) => toCamelCase(response.data) as RevealSsn)
}

/* eslint-disable @typescript-eslint/naming-convention */
export function updateSsn(userId: string, payload: { ssn: string }) {
  const url = `${getVGSDomain()}/api/v3/users/${userId}/ssn/`
  return axios
    .post(url, { ssn_encrypted: payload.ssn }, { withCredentials: true })
    .then((response) => toCamelCase(response.data) as User)
}
/* eslint-enable @typescript-eslint/naming-convention */
